/**
 * Cron expression → short human label. The one schedule vocabulary for the
 * app: the three Tasks views, the task form and drawer, and the Workflows ›
 * Insights trigger column ("Schedule · daily 02:00").
 *
 * Before this was shared, each Tasks view had its own table, so a schedule
 * the New Task form could save ("Weekly (Monday)", `0 0 * * 1`) showed as
 * raw code in another view.
 */
const CRON_PRESETS: Record<string, string> = {
  "*/5 * * * *": "Every 5 minutes",
  "*/15 * * * *": "Every 15 minutes",
  "*/30 * * * *": "Every 30 minutes",
  "0 * * * *": "Every hour",
  "0 */2 * * *": "Every 2 hours",
  "0 */4 * * *": "Every 4 hours",
  "0 */6 * * *": "Every 6 hours",
  "0 0 * * *": "Daily at 00:00",
  "0 0 1 * *": "Monthly on the 1st at 00:00",
};

/**
 * The schedules offered when creating or editing a task, in the order shown.
 * Every value here has a label in cronToText, so whatever the form can save,
 * every view can name.
 */
export const SCHEDULE_PRESETS: { value: string; label: string }[] = [
  { value: "*/15 * * * *", label: "Every 15 minutes" },
  { value: "*/30 * * * *", label: "Every 30 minutes" },
  { value: "0 * * * *", label: "Every hour" },
  { value: "0 */4 * * *", label: "Every 4 hours" },
  { value: "0 9 * * *", label: "Daily at 09:00" },
  { value: "0 9 * * 1-5", label: "Weekdays at 09:00" },
  { value: "0 17 * * 1-5", label: "Weekdays at 17:00" },
  { value: "0 9 * * 1", label: "Mondays at 09:00" },
  { value: "0 9 1 * *", label: "Monthly on the 1st at 09:00" },
];

/** Time zones offered for a task schedule. */
export const SCHEDULE_TIMEZONES: { value: string; label: string }[] = [
  { value: "Europe/London", label: "London" },
  { value: "UTC", label: "UTC" },
  { value: "Europe/Paris", label: "Paris" },
  { value: "America/New_York", label: "New York" },
  { value: "America/Los_Angeles", label: "Los Angeles" },
  { value: "Asia/Manila", label: "Manila" },
  { value: "Asia/Tokyo", label: "Tokyo" },
];

/** Short name for a time zone: "London", or the zone's own name. */
export function timezoneLabel(tz: string | null | undefined): string {
  if (!tz) return "UTC";
  return SCHEDULE_TIMEZONES.find((z) => z.value === tz)?.label ?? tz;
}

const DOW_PLURAL = ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"];

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

export function cronToText(expr: string | null | unknown): string {
  if (!expr) return "—";
  // Guard against object values stored before normalisation fix
  if (typeof expr === "object") {
    const obj = expr as Record<string, unknown>;
    const val = obj.expression ?? obj.cron ?? obj.schedule ?? obj.value ?? obj.every;
    return typeof val === "string" ? cronToText(val) : "—";
  }
  if (typeof expr !== "string") return "—";
  if (expr === "[object Object]") return "—";
  if (CRON_PRESETS[expr]) return CRON_PRESETS[expr];
  // Try to parse common patterns
  const parts = expr.trim().split(/\s+/);
  if (parts.length === 5) {
    const [min, hr, dom, mon, dow] = parts as [string, string, string, string, string];
    const everyMin = /^\*\/(\d+)$/.exec(min);
    if (everyMin && hr === "*" && dom === "*" && mon === "*" && dow === "*") {
      return `Every ${everyMin[1]} minutes`;
    }
    if (/^\d+$/.test(min) && /^\d+$/.test(hr) && mon === "*") {
      const time = `${hr.padStart(2, "0")}:${min.padStart(2, "0")}`;
      if (dom === "*") {
        if (dow === "*" || dow === "0-6") return `Daily at ${time}`;
        if (dow === "1-5") return `Weekdays at ${time}`;
        if (dow === "0,6" || dow === "6,0") return `Weekends at ${time}`;
        if (/^\d$/.test(dow)) return `${DOW_PLURAL[Number(dow) % 7]} at ${time}`;
      }
      if (/^\d+$/.test(dom) && dow === "*") return `Monthly on the ${ordinal(Number(dom))} at ${time}`;
    }
  }
  return expr;
}

/**
 * Compact variant for dense table cells: "daily 02:00", "weekdays 09:00",
 * "Mondays 09:00", "every 15 min".
 */
export function cronToShortText(expr: string | null | unknown): string {
  const long = cronToText(expr);
  return long
    .replace(/^Daily at /, "daily ")
    .replace(/^Weekdays at /, "weekdays ")
    .replace(/^Every (\d+) minutes$/, "every $1 min")
    .replace(/^Every (\d+) hours$/, "every $1h")
    .replace(/^Every hour$/, "hourly")
    .replace(/^Monthly on the (\w+) at /, "monthly $1 ")
    .replace(/ at /, " ");
}
